import { useFetchWrapper } from '_helpers';

export { useAttendanceActions };

function useAttendanceActions() {
    const fetchWrapper = useFetchWrapper();

    async function getSessions(classId) {
        const res = await fetchWrapper.get(`/api/classes/${classId}/attendance`, null, null);
        if (!res) return [];
        const json = await res.json();
        return json.status === 'Success' ? json.message : [];
    }

    async function createSession(classId, lesson_name, date, description, duration_min) {
        const body = new URLSearchParams();
        body.append('lesson_name', lesson_name);
        body.append('date', date);
        if (description) body.append('description', description);
        if (duration_min) body.append('duration_min', String(duration_min));
        const res = await fetchWrapper.post(`/api/classes/${classId}/attendance`, 'application/x-www-form-urlencoded', body);
        const json = await res.json();
        return json;
    }

    // Edit a session's own info (lesson name / description) — only provided keys are sent.
    async function updateSessionInfo(classId, sessionId, fields) {
        const res = await fetchWrapper.put(`/api/classes/${classId}/attendance/${sessionId}`, 'application/json', JSON.stringify(fields));
        return res.json();
    }

    // fields = { status?, comment?, homework?, attitude?, rating? } — only provided keys are sent.
    // Backend merges each field only when defined, so partial updates from either tab are safe.
    async function updateRecord(classId, sessionId, student_ref, fields) {
        const payload = JSON.stringify({ records: [{ student_ref, ...fields }] });
        const res = await fetchWrapper.put(`/api/classes/${classId}/attendance/${sessionId}`, 'application/json', payload);
        const json = await res.json();
        return json;
    }

    // Set many records in one PUT — records: [{ student_ref, status }]. Used by "cả lớp có mặt".
    async function bulkSetStatus(classId, sessionId, records) {
        const payload = JSON.stringify({ records });
        const res = await fetchWrapper.put(`/api/classes/${classId}/attendance/${sessionId}`, 'application/json', payload);
        return res.json();
    }

    // Re-project the lesson plan onto schedule dates (rolling calendar).
    async function regenerate(classId) {
        const res = await fetchWrapper.post(`/api/classes/${classId}/attendance/regenerate`, null, null);
        return res.json();
    }

    async function deleteSession(classId, sessionId) {
        const res = await fetchWrapper.delete(`/api/classes/${classId}/attendance/${sessionId}`, null, null);
        const json = await res.json();
        return json;
    }

    // Notify every currently absent/late student in a session ("finalize").
    async function notifyAbsentees(classId, sessionId) {
        const res = await fetchWrapper.post(`/api/classes/${classId}/attendance/${sessionId}/notify`, null, null);
        const json = await res.json();
        return json;
    }

    // Upload ONE proof photo per session (multipart). header=null → browser sets the boundary.
    async function uploadProof(classId, sessionId, file) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetchWrapper.post(`/api/classes/${classId}/attendance/${sessionId}/proof`, null, fd);
        return res.json();
    }

    async function deleteProof(classId, sessionId) {
        const res = await fetchWrapper.delete(`/api/classes/${classId}/attendance/${sessionId}/proof`, null, null);
        return res.json();
    }

    // Cancel a session (reason required). Backend sets cancelled=true + cancel_reason.
    async function cancelSession(classId, sessionId, reason) {
        const payload = JSON.stringify({ reason });
        const res = await fetchWrapper.post(`/api/classes/${classId}/attendance/${sessionId}/cancel`, 'application/json', payload);
        return res.json();
    }

    // Reverse a cancellation.
    async function uncancelSession(classId, sessionId) {
        const payload = JSON.stringify({ uncancel: true });
        const res = await fetchWrapper.post(`/api/classes/${classId}/attendance/${sessionId}/cancel`, 'application/json', payload);
        return res.json();
    }

    // Teacher presses "Có mặt" — stamps check-in; backend flags late + alerts managers if after start.
    async function checkIn(classId, sessionId) {
        const res = await fetchWrapper.post(`/api/classes/${classId}/attendance/${sessionId}/checkin`, null, null);
        return res.json();
    }

    // Manager presses "Đúng" (confirm=true) / un-confirms (confirm=false) teacher punctuality.
    async function confirmPunctuality(classId, sessionId, confirm = true) {
        const payload = JSON.stringify({ confirm });
        const res = await fetchWrapper.post(`/api/classes/${classId}/attendance/${sessionId}/punctuality`, 'application/json', payload);
        return res.json();
    }

    // Manager marks a session as taught ("Đã dạy") / undoes it — back-fills teach time when the
    // teacher's own check-in window (15 min either side of the lesson) had already closed.
    async function setSessionTaught(classId, sessionId, taught = true) {
        const payload = JSON.stringify({ taught });
        const res = await fetchWrapper.put(`/api/classes/${classId}/attendance/${sessionId}/teachtime`, 'application/json', payload);
        return res.json();
    }

    // Manager read-only overview: full attendance for any class (no membership gate).
    async function getManagerAttendance(classId) {
        const res = await fetchWrapper.get(`/api/manager/classes/${classId}/attendance`, null, null);
        if (!res) return [];
        const json = await res.json();
        return json.status === 'Success' ? json.message : [];
    }

    // Bulk-fill one displayed WeekCalendar week from schedule_slots, across all classes.
    // confirm=false → dry-run preview ({ toCreate, toSkip }); confirm=true → actually creates.
    async function regenerateWeek(from, to, confirm, classId) {
        const res = await fetchWrapper.post('/api/manager/sessions/regenerate-week', 'application/json', JSON.stringify({ from, to, confirm, classId }));
        return res.json();
    }

    return { getSessions, createSession, updateSessionInfo, updateRecord, bulkSetStatus, regenerate, regenerateWeek, deleteSession, notifyAbsentees, uploadProof, deleteProof, cancelSession, uncancelSession, checkIn, confirmPunctuality, setSessionTaught, getManagerAttendance };
}
